import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { teams } from '../lib/appwrite';
import Modal from '../components/Modal';
import LoadingOverlay from '../components/LoadingOverlay';
import { Users, UserPlus, Shield, ArrowRight, Trash2, Edit3 } from 'lucide-react';
import './PermissionsManagement.css';

const DEFAULT_ROLES = ['admin', 'founder', 'manager', 'salesOnly', 'production'];

export default function PermissionsManagement() {
    const { warehouseId, warehouseName, teamRoles } = useAuth();
    const navigate = useNavigate();

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [modal, setModal] = useState({ isOpen: false, message: '', type: 'success' });
    const [pendingAction, setPendingAction] = useState(null);

    // Invite form
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRoles, setInviteRoles] = useState([]);

    // Role edit modal
    const [editingMember, setEditingMember] = useState(null);
    const [editRoles, setEditRoles] = useState([]);

    const [allAvailableRoles] = useState(DEFAULT_ROLES);

    // Load members & custom roles
    const loadData = useCallback(async () => {
        if (!warehouseId) return;
        setLoading(true);
        try {
            const [membershipsRes] = await Promise.all([
                teams.listMemberships(warehouseId)
            ]);
            setMembers(membershipsRes.memberships);
        } catch (error) {
            console.error('Error loading data:', error);
            setModal({ isOpen: true, message: 'حدث خطأ في تحميل البيانات', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [warehouseId]);

    useEffect(() => {
        if (!teamRoles.includes('owner')) {
            navigate('/home');
            return;
        }
        loadData();
    }, [warehouseId, teamRoles, navigate, loadData]);

    // --- Invite Member ---
    const handleInvite = async () => {
        if (!inviteEmail.trim()) {
            setModal({ isOpen: true, message: 'يرجى إدخال البريد الإلكتروني', type: 'error' });
            return;
        }
        if (inviteRoles.length === 0) {
            setModal({ isOpen: true, message: 'يرجى اختيار صلاحية واحدة على الأقل', type: 'error' });
            return;
        }
        setProcessing(true);
        try {
            await teams.createMembership(
                warehouseId,
                inviteRoles,
                inviteEmail.trim(),
                undefined, // userId
                undefined, // phone
                `${window.location.origin}/join-team` // redirect url for invitation acceptance
            );
            setModal({ isOpen: true, message: 'تم إرسال الدعوة بنجاح', type: 'success' });
            setInviteEmail('');
            setInviteRoles([]);
            await loadData();
        } catch (error) {
            console.error('Invite error:', error);
            setModal({ isOpen: true, message: `فشل الإرسال: ${error.message}`, type: 'error' });
        } finally {
            setProcessing(false);
        }
    };

    // --- Remove Member ---
    const confirmRemoveMember = (membership) => {
        setPendingAction({ type: 'remove', membership });
        setModal({
            isOpen: true,
            message: `هل أنت متأكد من إزالة "${membership.userName || membership.userEmail}" من الفريق؟`,
            type: 'confirm'
        });
    };

    const handleRemoveMember = async (membership) => {
        setProcessing(true);
        try {
            await teams.deleteMembership(warehouseId, membership.$id);
            setModal({ isOpen: true, message: 'تم إزالة العضو بنجاح', type: 'success' });
            await loadData();
        } catch (error) {
            console.error('Remove error:', error);
            setModal({ isOpen: true, message: `فشلت العملية: ${error.message}`, type: 'error' });
        } finally {
            setProcessing(false);
        }
    };

    // --- Edit Roles ---
    const openEditRoles = (membership) => {
        setEditingMember(membership);
        setEditRoles([...membership.roles]);
    };

    const toggleEditRole = (role) => {
        setEditRoles(prev =>
            prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
        );
    };

    const saveRoles = async () => {
        if (!editingMember) return;
        if (editRoles.length === 0) {
            setModal({ isOpen: true, message: 'يجب تحديد صلاحية واحدة على الأقل', type: 'error' });
            return;
        }
        setProcessing(true);
        try {
            await teams.updateMembership(warehouseId, editingMember.$id, editRoles);
            setModal({ isOpen: true, message: 'تم تحديث الصلاحيات بنجاح', type: 'success' });
            setEditingMember(null);
            await loadData();
        } catch (error) {
            console.error('Update roles error:', error);
            setModal({ isOpen: true, message: `فشل التحديث: ${error.message}`, type: 'error' });
        } finally {
            setProcessing(false);
        }
    };



    // --- Invite role toggle ---
    const toggleInviteRole = (role) => {
        setInviteRoles(prev =>
            prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
        );
    };

    // --- Modal handlers ---
    const onConfirmAction = async () => {
        if (pendingAction?.type === 'remove') {
            await handleRemoveMember(pendingAction.membership);
        }
        closeModal();
    };

    const closeModal = () => {
        setModal(prev => ({ ...prev, isOpen: false }));
        setPendingAction(null);
    };

    return (
        <div className="permissions-root" dir="rtl">
            <LoadingOverlay isLoading={processing || loading} />
            <Modal
                isOpen={modal.isOpen}
                message={modal.message}
                type={modal.type}
                onClose={closeModal}
                onConfirm={pendingAction ? onConfirmAction : null}
            />

            {/* Role Edit Modal */}
            {editingMember && (
                <div className="role-edit-overlay" onClick={() => setEditingMember(null)}>
                    <div className="role-edit-modal" onClick={e => e.stopPropagation()}>
                        <h3><Shield size={20} /> تعديل الصلاحيات</h3>
                        <p className="member-name">{editingMember.userName || editingMember.userEmail}</p>
                        <div className="roles-checklist">
                            {allAvailableRoles.map(role => (
                                <label key={role} className="role-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={editRoles.includes(role)}
                                        onChange={() => toggleEditRole(role)}
                                    />
                                    {role}
                                </label>
                            ))}
                        </div>
                        <div className="modal-actions">
                            <button className="save-btn" onClick={saveRoles}>حفظ</button>
                            <button className="cancel-btn" onClick={() => setEditingMember(null)}>إلغاء</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="perm-container">
                {/* Header */}
                <div className="perm-header">
                    <h1>إدارة الصلاحيات — {warehouseName}</h1>
                    <button className="back-btn" onClick={() => navigate('/home')}>
                        <ArrowRight size={18} />
                        الرجوع
                    </button>
                </div>

                {/* Members Section */}
                <div className="perm-section">
                    <h2><Users size={22} /> المستخدمين داخل المخزن ({members.length})</h2>
                    {members.length === 0 ? (
                        <div className="empty-state">
                            <Users size={48} />
                            <p>لا يوجد أعضاء</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop: Table */}
                            <table className="members-table desktop-only-table">
                                <thead>
                                    <tr>
                                        <th>الاسم</th>
                                        <th>البريد الإلكتروني</th>
                                        <th>الصلاحيات</th>
                                        <th>الحالة</th>
                                        <th>إجراءات</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map(member => (
                                        <tr key={member.$id}>
                                            <td>{member.userName || '—'}</td>
                                            <td style={{ direction: 'ltr', textAlign: 'right' }}>{member.userEmail || '—'}</td>
                                            <td>
                                                <div className="role-tags">
                                                    {member.roles.map(role => (
                                                        <span
                                                            key={role}
                                                            className={`role-tag ${role === 'owner' ? 'owner-tag' : ''}`}
                                                        >
                                                            {role}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td>{member.confirm ? 'مؤكد' : 'في انتظار القبول'}</td>
                                            <td>
                                                <div className="action-btns">
                                                    <button
                                                        className="edit-role-btn"
                                                        onClick={() => openEditRoles(member)}
                                                        title="تعديل الصلاحيات"
                                                    >
                                                        <Edit3 size={14} /> تعديل
                                                    </button>
                                                    {!member.roles.includes('owner') && (
                                                        <button
                                                            className="remove-member-btn"
                                                            onClick={() => confirmRemoveMember(member)}
                                                            title="إزالة العضو"
                                                        >
                                                            <Trash2 size={14} /> إزالة
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Mobile: Cards */}
                            <div className="members-cards mobile-only-cards">
                                {members.map(member => (
                                    <div key={member.$id} className="member-card">
                                        <div className="member-card-header">
                                            <div className="member-card-name">{member.userName || '—'}</div>
                                            <span className={`member-card-status ${member.confirm ? 'confirmed' : 'pending'}`}>
                                                {member.confirm ? 'مؤكد' : 'في انتظار القبول'}
                                            </span>
                                        </div>
                                        <div className="member-card-email">{member.userEmail || '—'}</div>
                                        <div className="member-card-roles">
                                            {member.roles.map(role => (
                                                <span
                                                    key={role}
                                                    className={`role-tag ${role === 'owner' ? 'owner-tag' : ''}`}
                                                >
                                                    {role}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="member-card-actions">
                                            <button
                                                className="edit-role-btn"
                                                onClick={() => openEditRoles(member)}
                                            >
                                                <Edit3 size={14} /> تعديل الصلاحيات
                                            </button>
                                            {!member.roles.includes('owner') && (
                                                <button
                                                    className="remove-member-btn"
                                                    onClick={() => confirmRemoveMember(member)}
                                                >
                                                    <Trash2 size={14} /> إزالة
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Invite Section */}
                <div className="perm-section">
                    <h2><UserPlus size={22} /> دعوة مستخدم جديد</h2>
                    <div className="invite-form">
                        <div className="form-field">
                            <label>البريد الإلكتروني</label>
                            <input
                                type="email"
                                value={inviteEmail}
                                onChange={e => setInviteEmail(e.target.value)}
                                placeholder="example@email.com"
                                style={{ direction: 'ltr', textAlign: 'right' }}
                            />
                        </div>
                    </div>
                    <div style={{ marginTop: 14 }}>
                        <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#555' }}>اختر الصلاحيات:</label>
                        <div className="roles-checklist">
                            {allAvailableRoles.map(role => (
                                <label key={role} className="role-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={inviteRoles.includes(role)}
                                        onChange={() => toggleInviteRole(role)}
                                    />
                                    {role}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div style={{ marginTop: 16 }}>
                        <button
                            className="invite-btn"
                            onClick={handleInvite}
                            disabled={processing}
                        >
                            <UserPlus size={18} style={{ verticalAlign: 'middle', marginLeft: 6 }} />
                            إرسال الدعوة
                        </button>
                    </div>
                </div>

                {/* Roles Explanation Section */}
                <div className="perm-section explanations-section">
                    <h2><Shield size={22} /> شرح الصلاحيات</h2>
                    <div className="roles-explanation-grid">
                        {/* Admin */}
                        <div className="role-explanation-card">
                            <div className="role-card-header admin-header">
                                <Shield size={20} />
                                <h3>Admin</h3>
                            </div>
                            <div className="role-card-body">
                                <div className="perm-item true">وصول كامل وبدون حدود</div>
                                <div className="perm-item true">الوصول لتطبيق Amanak Manager</div>
                                <div className="perm-item true">تعديل الأصناف وإدارتها</div>
                                <div className="perm-item true">تعديل المعاملات السابقة</div>
                                <div className="perm-item true">الوصول لنظام الإنتاج</div>
                                <div className="perm-item true">الوصول لنظام المبيعات</div>
                                <div className="perm-item true">رؤية التقارير والمخزون</div>
                                <div className="perm-item true">إجراء معاملات</div>
                            </div>
                        </div>

                        {/* Founder */}
                        <div className="role-explanation-card">
                            <div className="role-card-header founder-header">
                                <Shield size={20} />
                                <h3>Founder</h3>
                            </div>
                            <div className="role-card-body">
                                <div className="perm-item true">صلاحيات شبه كاملة</div>
                                <div className="perm-item true">الوصول لتطبيق Amanak Manager</div>
                                <div className="perm-item false">تعديل الأصناف وإدارتها</div>
                                <div className="perm-item true">تعديل المعاملات السابقة</div>
                                <div className="perm-item true">الوصول لنظام الإنتاج</div>
                                <div className="perm-item true">الوصول لنظام المبيعات</div>
                                <div className="perm-item true">رؤية التقارير والمخزون</div>
                                <div className="perm-item true">إجراء معاملات</div>
                            </div>
                        </div>

                        {/* Manager */}
                        <div className="role-explanation-card">
                            <div className="role-card-header manager-header">
                                <Shield size={20} />
                                <h3>Manager</h3>
                            </div>
                            <div className="role-card-body">
                                <div className="perm-item false">يملك وصول مقيد ومحدود</div>
                                <div className="perm-item false">الوصول لتطبيق Amanak Manager</div>
                                <div className="perm-item false">تعديل الأصناف وإدارتها</div>
                                <div className="perm-item false">تعديل المعاملات السابقة</div>
                                <div className="perm-item true">الوصول لنظام الإنتاج</div>
                                <div className="perm-item true">الوصول لنظام المبيعات</div>
                                <div className="perm-item true">رؤية التقارير والمخزون</div>
                                <div className="perm-item true">إجراء معاملات</div>
                            </div>
                        </div>

                        {/* SalesOnly */}
                        <div className="role-explanation-card">
                            <div className="role-card-header sales-header">
                                <Shield size={20} />
                                <h3>SalesOnly</h3>
                            </div>
                            <div className="role-card-body">
                                <div className="perm-item false">يملك وصول مقيد ومحدود</div>
                                <div className="perm-item false">الوصول لتطبيق Amanak Manager</div>
                                <div className="perm-item false">تعديل الأصناف وإدارتها</div>
                                <div className="perm-item false">تعديل المعاملات السابقة</div>
                                <div className="perm-item false">الوصول لنظام الإنتاج</div>
                                <div className="perm-item true">الوصول لنظام المبيعات</div>
                                <div className="perm-item false">رؤية التقارير والمخزون</div>
                                <div className="perm-item true">إجراء معاملات</div>
                            </div>
                        </div>

                        {/* Production */}
                        <div className="role-explanation-card">
                            <div className="role-card-header production-header">
                                <Shield size={20} />
                                <h3>Production</h3>
                            </div>
                            <div className="role-card-body">
                                <div className="perm-item false">يملك وصول مقيد ومحدود</div>
                                <div className="perm-item false">الوصول لتطبيق Amanak Manager</div>
                                <div className="perm-item false">تعديل الأصناف وإدارتها</div>
                                <div className="perm-item false">تعديل المعاملات السابقة</div>
                                <div className="perm-item true">الوصول لنظام الإنتاج</div>
                                <div className="perm-item false">الوصول لنظام المبيعات</div>
                                <div className="perm-item false">رؤية التقارير والمخزون</div>
                                <div className="perm-item false">إجراء معاملات</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
